import {
    connectionHasEdges,
    connectionHasNodes,
    connectionHasPageInfo,
    edgeHasNode,
    hasTotalCount,
} from './typeguards';

import type {
    AnyConnectionType,
    AnyEdgeType,
    EdgeForConnection,
    NodeForConnection,
    PageInfoForConnection,
} from '../derived';

export function getTotalCount(
    connection: AnyConnectionType | null | undefined
): number | null {
    return hasTotalCount(connection) ? connection.totalCount : null;
}

/**
 * Returns the edges of the current page in a connection object. It will give an empty array if no edges were defined.
 *
 * @param connection
 */
export function getConnectionEdges<C extends AnyConnectionType>(
    connection: C | null | undefined
): EdgeForConnection<C>[] {
    if (!connection) return [];
    return connectionHasEdges(connection) ? connection.edges : [];
}

/**
 * Returns the nodes of the current page in a connection object. It uses the following fallback rules:
 *  - It will give the nodes in the `edges` if the `nodes`-field was missing.
 *  - It will give an empty array if both the `edges` and the `nodes` were missing.
 *
 * @param connection
 */
export function getConnectionNodes<C extends AnyConnectionType>(
    connection: C | null | undefined
): NodeForConnection<C>[] {
    if (!connection) return [];
    return connectionHasNodes(connection)
        ? connection.nodes
        : getConnectionEdges(connection)
              .filter(
                  (
                      edge
                  ): edge is EdgeForConnection<C> & {
                      node: NodeForConnection<C>;
                  } => edgeHasNode(edge as AnyEdgeType)
              )
              .map((edge) => edge.node);
}

/**
 * Returns the page info of a connection. It gives an empty `PageInfo` object when the page info was undefined.
 * This fallback `PageInfo` is always `{ __typname: "PageInfo" }`.
 *
 * @param connection
 */
export function getConnectionPageInfo<C extends AnyConnectionType>(
    connection: C | null | undefined
): PageInfoForConnection<C> {
    if (connectionHasPageInfo(connection)) return connection.pageInfo;

    return {
        __typename: 'PageInfo',
    } as PageInfoForConnection<C>;
}
